/**
 * @file Controlled Drugs audit report and worksheet rendering.
 */

import { appData, state } from '../../app/state/state.js';
import { openPrintWindow } from '../../shared/reporting/printWindow.js';
import {
    formatDate,
    getLocalDateString,
} from "../../shared/utils/dateUtils.js";
import {
    roundToPrecision,
    sortDrugsByOrder,
    sortLocationsByOrder,
} from "./drugUtils.js";
import {
    sanitizeHTML,
} from "../../shared/utils/contentUtils.js";

/**
 * Formats an audit discrepancy value for table display.
 * @param {number} diff Discrepancy value.
 * @returns {string} Discrepancy HTML.
 */
export function formatDiscrepancy(diff) {
    if (diff === 0) {return '<span class="text-green-600">0</span>';}
    const sign = diff > 0 ? '+' : '';
    return `<span class="text-red-600">${sign}${roundToPrecision(diff)}</span>`;
}

/**
 * Renders audit stock count details for in-app display or print output.
 * @param {object | null} audit Audit document data.
 * @param {boolean} isWorksheet Whether to render a blank worksheet.
 * @returns {string} Audit details HTML.
 */
export function renderAuditDetails(audit, isWorksheet = false) {
    // If worksheet, we allow a null audit object (just needs access to locations/drugs from state)
    // If not worksheet, we need the audit object
    if (!isWorksheet && !audit) {return '';}

    // Note: We use both Tailwind classes (for in-app) and inline styles (for print view where Tailwind is missing)
    let html = `
        <div class="bg-white rounded-lg border border-gray-200 overflow-hidden mb-6">
            <div class="overflow-x-auto">
                <table class="min-w-full divide-y divide-gray-200 border-collapse table-fixed" style="width: 100%; border-collapse: collapse; table-layout: fixed; border: 2px solid #555;">
                    <thead class="bg-gray-50">
                        <tr>
                            <th scope="col" class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border border-gray-200 w-[15%]" style="width: 15%; border: 1px solid #ddd; border-bottom: 2px solid #555; padding: 8px; text-align: left; background-color: #f9fafb; font-weight: bold;">Location</th>
                            <th scope="col" class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border border-gray-200 w-[20%]" style="width: 20%; border: 1px solid #ddd; border-bottom: 2px solid #555; padding: 8px; text-align: left; background-color: #f9fafb; font-weight: bold;">Drug</th>
                            <th scope="col" class="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border border-gray-200 w-[10%]" style="width: 10%; border: 1px solid #ddd; border-bottom: 2px solid #555; padding: 8px; text-align: center; background-color: #f9fafb; font-weight: bold;">Expected</th>
                            ${!isWorksheet ? `
                            <th scope="col" class="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border border-gray-200 w-[10%]" style="width: 10%; border: 1px solid #ddd; border-bottom: 2px solid #555; padding: 8px; text-align: center; background-color: #f9fafb; font-weight: bold;">Actual</th>
                            <th scope="col" class="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border border-gray-200 w-[10%]" style="width: 10%; border: 1px solid #ddd; border-bottom: 2px solid #555; padding: 8px; text-align: center; background-color: #f9fafb; font-weight: bold;">Discrepancy</th>
                            <th scope="col" class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border border-gray-200 w-[35%]" style="width: 35%; border: 1px solid #ddd; border-bottom: 2px solid #555; padding: 8px; text-align: left; background-color: #f9fafb; font-weight: bold;">Notes</th>
                            ` : `
                            <th scope="col" class="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border border-gray-200 w-[10%]" style="width: 10%; border: 1px solid #ddd; border-bottom: 2px solid #555; padding: 8px; text-align: center; background-color: #f9fafb; font-weight: bold;">Actual</th>
                            <th scope="col" class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border border-gray-200 w-[45%]" style="width: 45%; border: 1px solid #ddd; border-bottom: 2px solid #555; padding: 8px; text-align: left; background-color: #f9fafb; font-weight: bold;">Notes</th>
                            `}
                        </tr>
                    </thead>
                    <tbody class="bg-white divide-y divide-gray-200">
    `;

    const locations = sortLocationsByOrder((appData.config?.drugs?.locations || []).filter(l => l.isActive !== false));
    const drugs = sortDrugsByOrder((appData.drugInventory || []).filter(d => d.isActive !== false));

    // Drugs are now sorted by admin order via sortDrugsByOrder helper
    // No extra sort needed here as the helper handles it.

    locations.forEach(loc => {
        // Collect all renderable rows for this location first
        const locationRows = [];

        drugs.forEach(drug => {
            let expected;
            let actual;
            let discrepancy;
            let note;
            const drugDisplayName = drug ? (drug.shortName || drug.name || 'Unknown') : 'Unknown';

            if (isWorksheet) {
                // For worksheet, we use current stock levels as expected
                expected = roundToPrecision(drug?.stockLevels?.[loc.id] || 0);
                // Only show if there is stock, or if configured to show all (assume show > 0 for now to reduce clutter)
                // Actually, for a worksheet, we probably want to see ALL active drugs for that location
                // But let's stick to positive stock for now to keep it clean, or 0 if it exists in audit?
                // Request implies standard audit list. Usually, we audit everything in stock.
                // Let's include everything that has stock > 0.
                if (expected > 0) {
                    locationRows.push({
                        drugName: drugDisplayName,
                        expected: expected,
                        actual: '', // Blank for worksheet
                        discrepancy: null,
                        note: ''
                    });
                }
            } else {
                // Standard Audit Report Logic
                const key = `${drug.id}_${loc.id}`;
                expected = audit.snapshot?.[key];
                actual = audit.actuals?.[key];
                note = audit.discrepancyNotes?.[key];

                // Only show row if expected > 0 OR actual is set (even if 0) OR note exists
                if ((expected !== undefined && expected !== 0) || (actual !== undefined) || note) {
                    const actualVal = actual !== undefined ? actual : '-';
                    discrepancy = actual !== undefined ? (actual - (expected || 0)) : null;

                    locationRows.push({
                        drugName: drugDisplayName,
                        expected: expected || 0,
                        actual: actualVal,
                        discrepancy: discrepancy,
                        note: note
                    });
                }
            }
        });

        // Render the collected rows with rowspan for location
        if (locationRows.length > 0) {
            locationRows.forEach((row, index) => {
                const isLastRow = index === locationRows.length - 1;
                // Add a thicker bottom border to the last row of the location group
                const bottomBorderStyle = isLastRow ? 'border-bottom: 2px solid #666;' : '';

                html += `<tr class="hover:bg-gray-50">`;

                // First row acts as the anchor for the merged location cell
                if (index === 0) {
                    html += `<td class="px-4 py-2 text-sm font-bold text-gray-900 border border-gray-200 align-top bg-gray-50 v-align-top" rowspan="${locationRows.length}" style="border: 1px solid #ddd; border-bottom: 2px solid #666; padding: 8px; vertical-align: top; background-color: #f9fafb; font-weight: bold;">
                        ${sanitizeHTML(loc.name)}
                    </td>`;
                }

                html += `
                        <td class="px-4 py-2 text-sm font-medium text-gray-900 border border-gray-200" style="border: 1px solid #ddd; ${bottomBorderStyle} padding: 8px;">${sanitizeHTML(row.drugName)}</td>
                        <td class="px-4 py-2 text-sm text-center text-gray-500 border border-gray-200" style="border: 1px solid #ddd; ${bottomBorderStyle} padding: 8px; text-align: center;">${row.expected}</td>
                        `;

                if (isWorksheet) {
                    html += `
                        <td class="px-4 py-2 text-sm text-center text-gray-700 border border-gray-200" style="border: 1px solid #ddd; ${bottomBorderStyle} padding: 8px; text-align: center;"></td>
                        <td class="px-4 py-2 text-sm text-gray-600 border border-gray-200" style="border: 1px solid #ddd; ${bottomBorderStyle} padding: 8px;"></td>
                    `;
                } else {
                    html += `
                        <td class="px-4 py-2 text-sm text-center text-gray-700 border border-gray-200" style="border: 1px solid #ddd; ${bottomBorderStyle} padding: 8px; text-align: center;">${row.actual}</td>
                        <td class="px-4 py-2 text-sm text-center font-bold border border-gray-200" style="border: 1px solid #ddd; ${bottomBorderStyle} padding: 8px; text-align: center; font-weight: bold;">${row.discrepancy !== null ? formatDiscrepancy(row.discrepancy) : '-'}</td>
                        <td class="px-4 py-2 text-sm text-gray-600 border border-gray-200" style="border: 1px solid #ddd; ${bottomBorderStyle} padding: 8px;">${row.note ? sanitizeHTML(row.note) : '-'}</td>
                    `;
                }

                html += `</tr>`;
            });
        }
    });

    html += `
                    </tbody>
                </table>
            </div>
        </div>
    `;

    return html;
}

/**
 * Opens a print window for a completed drug audit report.
 * @param {object} audit Audit document data.
 * @returns {void}
 */
export function printAuditReport(audit) {
    if (!audit) {return;}

    // Create print window
    const printWindow = openPrintWindow();
    if (!printWindow) {return;}

    const auditNumber = audit.auditNumber || 'N/A';
    const resultsStatus = audit.resultsStatus || 'unknown';
    const resultsLabel = resultsStatus === 'reconciled' ? 'Reconciled' : 'Discrepancy';
    const startedBy = appData.users?.find(u => u.id === audit.startedBy);
    const finalizedBy = appData.users?.find(u => u.id === audit.finalizedBy);
    const startedByName = startedBy ? startedBy.name : 'Unknown';
    const finalizedByName = finalizedBy ? finalizedBy.name : 'Unknown';

    // Handle audit date logic
    let auditDateStr = '';
    const dateField = audit.finalizedAt || audit.startedAt;
    if (dateField) {
        if (dateField.toDate && typeof dateField.toDate === 'function') {
            auditDateStr = getLocalDateString(dateField.toDate());
        } else if (dateField.seconds) {
            auditDateStr = getLocalDateString(new Date(dateField.seconds * 1000));
        } else if (typeof dateField === 'string') {
            auditDateStr = dateField.split('T')[0];
        }
    }
    if (!auditDateStr) {auditDateStr = getLocalDateString();}
    const auditDate = formatDate(auditDateStr);

    // Get audit details HTML
    let detailsHtml = renderAuditDetails(audit);
    // Remove any buttons from details HTML for print view
    detailsHtml = detailsHtml.replace(/<button.*?>.*?<\/button>/gi, '');

    // Sanitize names for HTML
    const safeAuditNumber = sanitizeHTML(String(auditNumber));
    const safeResultsLabel = sanitizeHTML(resultsLabel);
    const safeStartedByName = sanitizeHTML(startedByName);
    const safeFinalizedByName = sanitizeHTML(finalizedByName);
    const safeAuditDate = sanitizeHTML(auditDate);

    printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
            <title>Audit #${safeAuditNumber} - ${safeAuditDate}</title>
            <style>
                body { font-family: Arial, sans-serif; margin: 20px; }
                h1 { color: #333; border-bottom: 2px solid #333; padding-bottom: 10px; }
                h2 { color: #666; margin-top: 20px; }
                table { width: 100%; border-collapse: collapse; margin-top: 10px; }
                th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
                th { background-color: #f2f2f2; font-weight: bold; }
                .location-header { background-color: #e0e0e0; font-weight: bold; }
                .text-green-600 { color: #16a34a; }
                .text-red-600 { color: #dc2626; }
                .info-section { margin: 20px 0; }
                .info-row { margin: 5px 0; }
                @media print {
                    body { margin: 0; }
                    @page {
                        margin: 1cm;
                        size: landscape;
                    }
                }
            </style>
        </head>
        <body>
            <h1>Audit #${safeAuditNumber}</h1>
            <div class="info-section">
                <div class="info-row"><strong>Date:</strong> ${safeAuditDate}</div>
                <div class="info-row"><strong>Results:</strong> ${safeResultsLabel}</div>
                <div class="info-row"><strong>Conducted by:</strong> ${safeStartedByName}</div>
                <div class="info-row"><strong>Approved by:</strong> ${safeFinalizedByName}</div>
            </div>
            ${detailsHtml}
            <script>
                window.onload = function() {
                    window.print();
                }
            </script>
        </body>
        </html>
    `);
    printWindow.document.close();
}

/**
 * Opens a print window for a blank drug audit worksheet.
 * @returns {void}
 */
export function printAuditWorksheet() {
    // Create print window
    const printWindow = openPrintWindow();
    if (!printWindow) {return;}

    const currentDate = formatDate(getLocalDateString());
    const currentUser = appData.currentUserProfile?.name || state.currentUser?.email || 'Start Audit';

    // Get audit worksheet HTML (pass true for isWorksheet)
    const detailsHtml = renderAuditDetails(null, true);

    const safeAuditDate = sanitizeHTML(currentDate);
    const safeUserName = sanitizeHTML(currentUser);

    printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
            <title>Audit Worksheet - ${safeAuditDate}</title>
            <style>
                body { font-family: Arial, sans-serif; margin: 20px; }
                h1 { color: #333; border-bottom: 2px solid #333; padding-bottom: 10px; }
                h2 { color: #666; margin-top: 20px; }
                table { width: 100%; border-collapse: collapse; margin-top: 10px; }
                th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
                th { background-color: #f2f2f2; font-weight: bold; }
                .location-header { background-color: #e0e0e0; font-weight: bold; }
                .text-green-600 { color: #16a34a; }
                .text-red-600 { color: #dc2626; }
                .info-section { margin: 20px 0; }
                .info-row { margin: 5px 0; }
                @media print {
                    body { margin: 0; }
                    @page {
                        margin: 1cm;
                        size: landscape;
                    }
                }
            </style>
        </head>
        <body>
            <h1>Audit Worksheet</h1>
            <div class="info-section">
                <div class="info-row"><strong>Date:</strong> ${safeAuditDate}</div>
                <div class="info-row"><strong>Printed by:</strong> ${safeUserName}</div>
            </div>
            ${detailsHtml}
            <script>
                window.onload = function() {
                    window.print();
                }
            </script>
        </body>
        </html>
    `);
    printWindow.document.close();
}
